export type MixpanelRequest = {
  token: string;
  endpoint: string;
  data: unknown[];
  serverURL: string;
  useIPAddressForGeoLocation: boolean;
};

class AnalyticsDeliveryError extends Error {
  // The SDK retains the batch unless code is 400 (which deletes events).
  code = 503;
}

/** SDK transport only: never replaces window.fetch or changes tracking opt-outs. */
export function createMixpanelTransport(
  backendURL: string,
  fetchRequest: typeof fetch = (input, init) => fetch(input, init),
  now: () => number = Date.now,
) {
  let useBackend = false;
  let retryAfter = 0;
  let pending = Promise.resolve();
  const activeEndpoints = new Set<string>();

  const post = async (url: string, body: string, contentType: string) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10_000);
    try {
      const response = await fetchRequest(url, {
        method: "POST",
        headers: { "Content-Type": contentType },
        body,
        signal: controller.signal,
      });
      if (!response.ok || (await response.json()) !== 1) {
        throw new AnalyticsDeliveryError("Analytics delivery was not accepted");
      }
    } finally {
      clearTimeout(timeout);
    }
  };

  return async (request: MixpanelRequest): Promise<void> => {
    if (!["/track/", "/engage/", "/groups/"].includes(request.endpoint)) {
      throw new AnalyticsDeliveryError("Unsupported analytics endpoint");
    }
    // The SDK can flush again before a prior batch finishes. Only that first
    // caller may acknowledge/splice the batch; defer overlapping flushes.
    if (activeEndpoints.has(request.endpoint)) {
      throw new AnalyticsDeliveryError("Analytics batch is already in flight");
    }
    activeEndpoints.add(request.endpoint);
    const delivery = pending.then(async () => {
      if (now() < retryAfter) {
        throw new AnalyticsDeliveryError("Analytics delivery is cooling down");
      }
      if (!useBackend) {
        try {
          await post(
            `${request.serverURL}${request.endpoint}?ip=${+request.useIPAddressForGeoLocation}`,
            `data=${encodeURIComponent(JSON.stringify(request.data))}`,
            "application/x-www-form-urlencoded",
          );
          return;
        } catch {
          useBackend = true;
        }
      }
      try {
        await post(
          `${backendURL}${request.endpoint}`,
          JSON.stringify({ data: request.data }),
          "application/json",
        );
      } catch {
        retryAfter = now() + 60_000;
        throw new AnalyticsDeliveryError("Analytics backend is unavailable");
      }
    });
    // Serialize endpoint batches so only one direct failure is needed to switch.
    pending = delivery.catch(() => {});
    try {
      await delivery;
    } finally {
      activeEndpoints.delete(request.endpoint);
    }
  };
}
